import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { ArticlesModule, LAYOUT_TYPES } from 'site-modules/shared/components/articles-module/articles-module';
import { HelpfulTipsModule } from 'site-modules/shared/components/helpful-tips-module/helpful-tips-module';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { ResearchSharedSubnav } from 'site-modules/shared/components/editorial/research-shared-subnav/research-shared-subnav';
import { ArticleVideos } from 'site-modules/editorial/components/article-videos/article-videos';
import { ArticlePhoto } from 'site-modules/shared/components/editorial/article-photo/article-photo';
import { transformContentForFaq } from 'site-modules/shared/components/faq/utils';
import { WRAPPER_TYPES } from 'site-modules/shared/components/faq/constants';
import { Faq } from 'site-modules/shared/components/faq/faq';
import { VehicleSection } from 'site-modules/editorial/components/research-category-landing/vehicle-section/vehicle-section';
import { ErrorBoundary } from 'site-modules/shared/components/error-boundary/error-boundary';
import { HomeMarquee } from 'site-modules/shared/components/upper-funnel/home-marquee/home-marquee';
import { VehicleShowcase } from 'site-modules/shared/components/upper-funnel/vehicle-showcase/vehicle-showcase';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { MoreOnEdmunds } from 'site-modules/shared/components/editorial/more-on-edmunds/more-on-edmunds';
import { Ratings } from 'site-modules/editorial/components/ratings/ratings';
import { AdEntry } from 'site-modules/editorial/components/ad-entry/ad-entry';
import { ArticleVideo } from 'site-modules/editorial/components/article-video/article-video';

import { Article } from './article';
import { ArticlesList } from './articles-list';
import { ResearchSharedCalculators } from './research-shared-calculators';
import { VideosGrid } from './videos-grid/videos-grid';

import { ENTRY_RENDER_TYPE, ENTRY_SHARED_ID, ENTRY_ID } from './constants';

import './research-entry.scss';

export function ResearchEntry({
  entry,
  hasSeparator,
  pathname,
  openVideoModal,
  isMobile,
  isTitleEvenCount,
  linkWidgetContent,
}) {
  const entryRenderType = entry.metadata('renderType').value();
  let renderEntry;

  if (entryRenderType) {
    // Entry with known renderType
    switch (entryRenderType) {
      case ENTRY_RENDER_TYPE.DEFAULT:
        renderEntry = (
          <section data-tracking-parent="article-module">
            <ArticlesModule content={entry} />
          </section>
        );
        break;
      case ENTRY_RENDER_TYPE.GRID:
        renderEntry = <ArticlesModule content={entry} layoutType={LAYOUT_TYPES.grid} />;
        break;
      case ENTRY_RENDER_TYPE.LIST:
        renderEntry = (
          <Fragment>
            <MrectNative
              position={'1'}
              refreshable={false}
              wiredBreakpoints={{ md: true }}
              wrapperClass="mrect mx-auto mb-1_5"
            />
            <ArticlesList content={entry} noMoreLink={!entry.metadata('moreLink').value()} className="mb-1_5" />
          </Fragment>
        );
        break;
      case ENTRY_RENDER_TYPE.VIDEO: {
        const { title: videoHeading } = entry;
        const videoTitle = entry.metadata('videoTitle').value();
        const description = entry.metadata('videoDescriptionHtml').value();

        renderEntry = (
          <Fragment>
            <h2 className="heading-3 mb-0_5">{videoHeading}</h2>
            <ArticleVideo entry={entry} />
            <h3 className="heading-5 mt-1_5 mb-1">{videoTitle}</h3>
            <ContentFragment classes="content-fragment size-16 text-gray-darker mb-3_5 mb-md-0">
              {description}
            </ContentFragment>
          </Fragment>
        );
        break;
      }
      case ENTRY_RENDER_TYPE.VIDEOS:
        renderEntry = <ArticleVideos entry={entry} isMobile={isMobile} />;
        break;
      case ENTRY_RENDER_TYPE.VIDEOS_GRID:
        renderEntry = <VideosGrid entry={entry} />;
        break;
      case ENTRY_RENDER_TYPE.LINKS_LIST:
        renderEntry = <HelpfulTipsModule content={entry} className="px-lg-0_5 mb-1_5" />;
        break;
      case ENTRY_RENDER_TYPE.AD:
        switch (entry.id) {
          case ENTRY_SHARED_ID.EDITORIAL_HIGH_IMPACT:
            renderEntry = (
              <Fragment>
                {isTitleEvenCount && (
                  <ErrorBoundary>
                    <hr className="mt-2 mb-1_5 hidden-lg-up" />
                    <HomeMarquee />
                    <hr className="mt-0 col-12 mb-2 hidden-lg-up" />
                  </ErrorBoundary>
                )}
                {!isTitleEvenCount && (
                  <ErrorBoundary>
                    <hr className="mt-2 mb-1_5 hidden-lg-up" />
                    <VehicleShowcase
                      content={linkWidgetContent.child('home-vehicle-showcase-v2')}
                      bottomBorderHiddenLgUp
                    />
                  </ErrorBoundary>
                )}
              </Fragment>
            );
            break;
          default:
            renderEntry = (
              <div className="mb-1_5">
                <AdEntry entry={entry} isMobile={isMobile} />
              </div>
            );
        }
        break;
      case ENTRY_RENDER_TYPE.HTML:
        switch (true) {
          case entry.id === ENTRY_ID.ARTICLES: {
            const articles = entry.children();
            const { title } = entry;
            renderEntry = (
              <section>
                <h2 className="heading-2 mb-1_5">{title}</h2>
                {articles.map((article, index) => (
                  <Fragment key={article.id}>
                    <Article article={article} />
                    {index !== articles.length - 1 && <hr className="mt-1_5 mb-2" />}
                  </Fragment>
                ))}
              </section>
            );
            break;
          }
          case entry.id.includes(ENTRY_ID.HTML): {
            const { title } = entry;
            renderEntry = (
              <section className="content-section">
                <h2 className="heading-3">{title}</h2>
                <ContentFragment classes="content-fragment size-16 text-gray-darker">
                  {entry.metadata('htmlContent').value()}
                </ContentFragment>
              </section>
            );
            break;
          }
          default:
            renderEntry = null;
        }
        break;

      case ENTRY_RENDER_TYPE.CARD: {
        renderEntry = <VehicleSection entry={entry} openVideoModal={openVideoModal} isMobile={isMobile} />;
        break;
      }

      default:
        renderEntry = null;
    }
  } else if (entry.id) {
    // Shared modules known by entry id
    switch (entry.id) {
      case ENTRY_SHARED_ID.MORE_ON_EDMUNDS: {
        renderEntry = <MoreOnEdmunds />;
        break;
      }
      case ENTRY_SHARED_ID.RESEARCH_SUBNAV: {
        renderEntry = <ResearchSharedSubnav />;
        break;
      }
      case ENTRY_SHARED_ID.CALCULATORS: {
        renderEntry = <ResearchSharedCalculators />;
        break;
      }
      case ENTRY_SHARED_ID.FAQ: {
        renderEntry = (
          <Faq
            {...transformContentForFaq(entry)}
            wrapperType={WRAPPER_TYPES.accordion}
            pathname={pathname}
            generateLearnMoreLinks
          />
        );
        break;
      }
      case ENTRY_SHARED_ID.PHOTO: {
        renderEntry = (
          <section data-tracking-parent="card-photo">
            <h2 className="heading-3 mb-1_5">{entry.title}</h2>
            <ArticlePhoto photos={entry.links()} className="mb-1_5" />
          </section>
        );
        break;
      }
      default:
        renderEntry = null;
    }

    if (entry.id.includes(ENTRY_ID.RATINGS)) {
      renderEntry = <Ratings ratingsContentEntry={entry} />;
    }
  }

  if (!renderEntry) {
    return null;
  }

  return (
    <div className="category-landing-entry">
      {renderEntry}
      {hasSeparator && <hr className="mt-1_5 mb-2 hidden-lg-down" />}
    </div>
  );
}

ResearchEntry.propTypes = {
  openVideoModal: PropTypes.func,
  entry: CmsEntities.Content,
  linkWidgetContent: CmsEntities.Content,
  hasSeparator: PropTypes.bool,
  isMobile: PropTypes.bool,
  isTitleEvenCount: PropTypes.bool,
};

ResearchEntry.defaultProps = {
  openVideoModal: noop,
  entry: DEFAULT_CONTENT,
  linkWidgetContent: DEFAULT_CONTENT,
  hasSeparator: false,
  isMobile: false,
  isTitleEvenCount: false,
};
